import React, { createRef, useCallback, useEffect, useState } from "react";
import { StyleSheet, View, ScrollView, Text } from "react-native";
import { colors } from "src/assets";

import {
  Typography,
  FormControlLabel,
  FormControlGroup,
  AmountBanner,
  BottomActions,
  Button,
  ActionDialog,
} from "src/components";
import {
  useAccount,
  useTransactions,
  useTransactionBuy,
  useCognito,
} from "src/hooks";
import { stringDateFullDynamicFormat } from "src/utils/date";
import { toCurrency } from "src/utils/formatter";
import LocalText from "src/local";
import alert from "src/components/alert";
import { useNavigation } from "@react-navigation/native";
import { RootStackScreenProps } from "src/navigation/types";

export type BalanceNavigationParam = undefined;

const points: { key: string; label: string }[] = [
  { key: "500", label: "￥500" },
  { key: "1000", label: "￥1,000" },
  { key: "3000", label: "￥3,000" },
  { key: "5000", label: "￥5,000" },
  { key: "10000", label: "￥10,000" },
];

export const BalanceScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"Balance">["navigation"]>();
  const [account] = useAccount();
  const [items] = useTransactions();
  const [buyRequestHandler, buyRequestLoading] = useTransactionBuy();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [username] = useCognito();

  const buyHandler = useCallback(
    async (index: number) => {
      if (index !== points.length && username) {
        alert(
          LocalText.screens.balance.buyPointConfirmAlertTitle,
          `${points[index].label}${LocalText.screens.balance.buyPointConfirmAlertDescription}`,
          [
            { text: LocalText.common.cancel, style: "cancel" },
            {
              text: LocalText.common.ok,
              onPress: async () => {
                if (await buyRequestHandler(username, points[index].key)) {
                  alert(LocalText.screens.balance.buyPointAlertSuccessTitle);
                }
              },
            },
          ],
          { cancelable: false }
        );
      }
    },
    [buyRequestHandler, username]
  );

  return (
    <View style={styles.root}>
      <ScrollView style={styles.scrollView}>
        {account && (
          <AmountBanner
            containerStyle={styles.banner}
            text={LocalText.screens.balance.balance}
            value={account?.balance}
          />
        )}
        {account?.stipeCard && (
          <React.Fragment>
            <Typography
              text={LocalText.screens.balance.cardDetail}
              variant={"section"}
            />
            <FormControlGroup>
              <FormControlLabel
                text={`**** - **** - **** - ${account?.stipeCard}`}
                detail={account?.stipeExpired}
                rightIcon={"chevron"}
                onPress={() => navigation.navigate("CreditCard")}
              />
            </FormControlGroup>
          </React.Fragment>
        )}

        <Typography
          text={LocalText.screens.balance.usageHistory}
          variant={"section"}
        />
        <FormControlGroup>
          {items.map((item) => (
            <FormControlLabel
              key={item.id}
              text={stringDateFullDynamicFormat(item.createdAt)}
              detail={
                item.amount !== undefined && item.amount > 0
                  ? `+ ¥${toCurrency(item.amount)}`
                  : `- ¥${toCurrency((item.amount || -0) * -1)}`
              }
            />
          ))}
        </FormControlGroup>
      </ScrollView>
      <BottomActions>
        <Button
          variant={"secondary"}
          icon={"chevron"}
          iconColor={colors.chokuRed}
          iconSize={12}
          title={LocalText.common.back}
          onPress={() => navigation.goBack()}
        />

        {account?.stipeCard ? (
          <Button
            variant={"primary"}
            icon={"yen"}
            loading={buyRequestLoading}
            title={LocalText.screens.balance.buyPoint}
            onPress={() => setOpenDialog(true)}
          />
        ) : (
          <Button
            variant={"primary"}
            title={LocalText.screens.balance.addCreditCard}
            onPress={() => navigation.navigate("CreditCard")}
          />
        )}
      </BottomActions>
      <ActionDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onAction={(index) => {
          setOpenDialog(false);
          buyHandler(index);
        }}
        items={points}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.gray1,
  },
  scrollView: {
    flex: 1,
  },
  banner: {
    marginHorizontal: 16,
    marginVertical: 8,
  },
  footer: {
    paddingTop: 0,
  },
  formGroup: {
    marginVertical: 8,
  },
});
